import { from } from '@apollo/client';
import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  createHttpLink,
  type DefaultContext,
  InMemoryCache,
  type MutationOptions,
  type Operation,
  type OperationVariables,
  type QueryOptions,
  type ServerError,
} from '@apollo/client/core';
import { print } from '@apollo/client/utilities';
import { type ApolloProvider, createApolloProvider } from '@vue/apollo-option';
import { onError } from 'apollo-link-error';
import { RestLink } from 'apollo-link-rest';
import { removeTypenameFromMutationLink } from 'apollo-remove-typename-mutation-link';
import { v4 as uuidv4 } from 'uuid';
import { type App } from 'vue';
import crypto from 'crypto';
import { cancelRequestLink } from './cancel-request';
import type { AuthService } from '../auth';
import type { SnackErrorTextsKeys } from '@/parts/errors/utils/error-types';
import { useErrorMessage } from '@/core/composables/useErrorMessage';
import { useAppState } from '@/core/store/useAppState';
import { deepCloneObject, objectKeys } from '@/core/utils/object';
import { isFile, isObject } from '@/core/utils/type-guards';
import { type PageErrorKey, usePageErrorStore } from '@/parts/errors/store/pageErrorStore';

type ResponseHeaders = {
  traceparent: string;
  'trace-id'?: string;
};

export type ResponseError = {
  message: string;
  __typename: SnackErrorTextsKeys;
  extensions?: {
    code?: string;
  };
};

export type Context = {
  requestTrackerId?: string;
};

export type ResponseData<T extends Record<string, unknown>> = {
  data: T | null | undefined;
  errors: ResponseError[] | null | undefined;
};

export const isServerError = (error: ApolloError['networkError']): error is ServerError =>
  !!error && 'response' in error;

export const reloadPageErrorReasons = ['must_have_supplier', 'role_access_denied', ''];

export class CommandProcessor {
  public apolloProvider: ApolloProvider;
  public auth: AuthService;

  constructor(private readonly app: App) {
    this.auth = app.config.globalProperties.$auth;

    const cache = new InMemoryCache();

    const restLink = createRestLink();

    const errorLink = onError((error) => {
      console.error(error);

      const { graphQLErrors, networkError, operation } = error;

      if (graphQLErrors && Array.isArray(graphQLErrors)) {
        if (
          graphQLErrors.some(
            (graphQLError) =>
              graphQLError.extensions &&
              ['AUTH_NOT_AUTHENTICATED', 'PERMISSION_DENIED'].includes(graphQLError.extensions.code as string),
          )
        ) {
          void this.auth.logout();

          return;
        }
      }

      const status = networkError && isServerError(networkError) ? networkError.response.status : undefined;

      if (status === 401) {
        void this.auth.logout();

        return;
      }

      if (!graphQLErrors && !status && !window.navigator.onLine) {
        const { showOfflineRequestError } = useAppState();
        showOfflineRequestError();

        return;
      }

      const { setPageErrorKey } = usePageErrorStore();

      const definition = operation.query.definitions[0];
      const selection = 'selectionSet' in definition ? definition.selectionSet.selections[0] : undefined;
      const operationName = selection && 'name' in selection ? selection.name.value : undefined;

      if (operationName) {
        setPageErrorKey(operationName as PageErrorKey);
      }

      if (!networkError || !isServerError(networkError)) {
        return;
      }

      const context = operation.getContext();

      const gqlErrors = isObject(graphQLErrors)
        ? Object.keys(graphQLErrors as Record<string, string[]>).map((key) => ({
            title: key,
            message: (graphQLErrors as Record<string, string[]>)[key][0],
          }))
        : graphQLErrors;

      const errors = (gqlErrors || [error.networkError]) as unknown as ResponseError[];

      this.showError(
        errors,
        context,
        operation as Operation,
        networkError.result as ResponseError[],
        networkError.response.url,
        {
          traceparent: context?.headers?.traceparent,
        },
        networkError.response.status,
      );
    });

    const afterwareLink = new ApolloLink((operation, forward) =>
      forward(operation).map((response) => {
        const context = operation.getContext();

        const responseKeys = objectKeys(response.data);

        const errors = response.data?.[responseKeys[0]]?.errors;

        if (errors) {
          this.showError(
            errors,
            context,
            operation,
            response as ResponseError[],
            context.response.url,
            {
              traceparent: context?.headers?.traceparent,
            },
            200,
          );
        }

        return response;
      }),
    );

    const httpLink = createHttpLink({
      uri: `${process.env.GATE_URL}graphql/`,
    });

    this.apolloProvider = createApolloProvider({
      defaultClient: new ApolloClient({
        link: from([
          removeTypenameFromMutationLink,
          errorLink as unknown as ApolloLink,
          afterwareLink,
          cancelRequestLink,
          restLink,
          httpLink,
        ]),
        cache,
        queryDeduplication: false,
      }),
    });
  }

  private showError(
    errors: ResponseError[],
    context: DefaultContext,
    operation: Operation,
    response: ResponseError[],
    url: string,
    headers: ResponseHeaders,
    status: number,
  ) {
    if (!headers['trace-id']) {
      console.error('trace-id less');
    }

    this.app.runWithContext(() => {
      const { showErrorMessage } = useErrorMessage();

      const traceId = headers.traceparent.split('-')[1];

      const payload = {
        errors,
        url,
        operation: {
          headers: context.headers,
          query: print(operation.query),
          variables: operation.variables,
        },
        response: {
          errors: response,
        },
        traceId,
        status,
      };

      showErrorMessage({
        requestError: payload,
      });
    });
  }

  private getClient() {
    return this.apolloProvider.defaultClient;
  }

  private getAuthToken() {
    return this.auth.getAccessToken();
  }

  private async getHeaders() {
    const tracId = uuidv4().replaceAll('-', '');
    const parentId = crypto.randomBytes(8).toString('hex');
    const traceparent = `00-${tracId}-${parentId}-01`;

    return {
      Authorization: (await this.getAuthToken()) || '',
      traceparent,
      'GraphQL-Preflight': 1,
    };
  }

  private processError(apolloError: ApolloError): {
    data: null;
    errors: ResponseError[];
  } {
    console.error(apolloError);
    // might be removed in future - need for reading error in console
    const error = deepCloneObject(apolloError);

    console.error(error);

    // При некоторых ошибках с серверы мы хотим перезагрузить страницу
    const errorReason =
      isServerError(error.networkError) &&
      typeof error.networkError?.result === 'object' &&
      error.networkError?.result?.errors?.[0]?.extensions?.reason;

    if (errorReason && reloadPageErrorReasons.includes(errorReason)) {
      window.location.reload();

      return {
        data: null,
        errors: [],
      };
    }

    let errors: ResponseError[] = [];

    if (isServerError(error.networkError) && 'result' in error.networkError) {
      const responseData = error.networkError.result as { errors?: ResponseError[] };

      if (responseData.errors) {
        errors = responseData.errors;
      }
    }

    return {
      data: null,
      errors,
    };
  }

  public async query<
    TResult extends Record<string, unknown>,
    TVariables extends OperationVariables = OperationVariables,
  >(
    query: QueryOptions<TVariables>['query'],
    variables?: TVariables,
    context?: Context,
  ): Promise<ResponseData<TResult>> {
    try {
      if (variables) {
        variables = processVariables(variables) as TVariables;
      }

      const response = await this.getClient().query<TResult, TVariables>({
        query,
        variables,
        context: {
          headers: await this.getHeaders(),
          ...context,
        },
        // TODO разобраться с кешем
        fetchPolicy: 'no-cache',
      });

      return {
        data: { ...response.data },
        errors: null,
      };
    } catch (error: unknown) {
      return this.processError(error as ApolloError);
    }
  }

  public async mutation<TResult extends object, TVariables extends OperationVariables>(
    mutation: MutationOptions['mutation'],
    variables?: TVariables,
  ) {
    try {
      if (variables) {
        variables = processVariables(variables) as TVariables;
      }

      const response = await this.getClient().mutate<TResult>({
        mutation,
        variables,
        context: {
          headers: await this.getHeaders(),
        },
      });

      const responseKeys = objectKeys(response.data);

      let errors: ResponseError[] | undefined = (
        response.data?.[responseKeys[0]] as {
          errors?: ResponseError[];
        }
      )?.errors;

      // process with apollo rest link
      if (response.errors) {
        errors = [{ message: response.errors[0].message, __typename: 'ApolloRestLinkError' }];
      }

      return {
        data: !errors && response.data ? { ...response.data } : undefined,
        errors: errors,
      };
    } catch (error: unknown) {
      return this.processError(error as ApolloError);
    }
  }
}

export function apiPlugin(app: App) {
  const commandProcessor = new CommandProcessor(app);

  app.config.globalProperties.$api = commandProcessor;

  app.use(commandProcessor.apolloProvider);
}

const blobContentTypes = ['application/pdf', 'application/x-zip-compressed', 'image/'];

function createRestLink() {
  return new RestLink({
    uri: `${process.env.GATE_URL}rest/spa`,
    endpoints: {
      suggestions: `${process.env.GATE_URL}suggestions/suggest`,
    },
    responseTransformer: async (response) => {
      try {
        if (!response) {
          return null;
        }

        const contentType = response.headers?.get('content-type');

        if (contentType && blobContentTypes.some((type) => contentType.includes(type))) {
          return {
            data: await response.blob(),
          };
        } else if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json();
        } else {
          return response.text?.();
        }
      } catch (e) {
        console.error(e);
      }
    },
    bodySerializers: {
      fileEncode: (data: Record<string, string | Blob>, headers: Headers) => {
        const formData = new FormData();

        const keys = objectKeys(data);

        keys.forEach((key) => {
          const value = data[key];

          if (value === undefined || value === null) {
            return;
          }

          if (Array.isArray(value) && value[0] && isFile(value[0])) {
            value.forEach((file) => {
              formData.append(key, file);
            });
          } else {
            formData.append(key, data[key]);
          }
        });

        return { body: formData, headers };
      },
    },
  });
}

export function processVariables(variables: OperationVariables) {
  if (!variables) {
    return variables;
  }

  const newVariables = {} as OperationVariables;

  const step = (vars: Record<string, unknown>, newVars: Record<string, unknown>) => {
    const keys = objectKeys(vars);

    keys.forEach((key) => {
      if (!(vars[key] === undefined || vars[key] === null)) {
        newVars[key] = vars[key];
      }

      if (isObject(vars[key])) {
        step(vars[key] as Record<string, unknown>, vars[key] as Record<string, unknown>);
      }
    });
  };

  step(variables, newVariables);

  return newVariables;
}
